/**
 * StoryPlayer - Main orchestrator component
 * Assembles all story components and manages the overall experience
 */

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStoryStore } from '../../store/storyStore';
import { SceneRenderer } from '../StoryPlayer/SceneRenderer';
import { CreditsRenderer } from '../StoryPlayer/CreditsRenderer';
import { DialogueBox } from '../Dialogue/DialogueBox';
import { ChoicePanel } from '../Dialogue/ChoicePanel';
import { AudioManager } from '../Audio/AudioManager';
import { NarrativeVideo } from '../Video/NarrativeVideo';
import { NarrativeImage } from '../Image/NarrativeImage';
import { AutoAdvanceManager } from '../Auto/AutoAdvanceManager';
import ShakeEffect from '../Effects/ShakeEffect';

interface StoryPlayerProps {
  onOpenSettings?: () => void;
  onOpenMenu?: () => void;
}

const StoryPlayer: React.FC<StoryPlayerProps> = ({ onOpenMenu }) => {
  const {
    currentScene,
    currentNarrative,
    availableChoices,
    isStoryCompleted,
    isLoading,
    isDialogueBoxVisible,
    isChoicePanelVisible,
    isTransitioning,
    advanceNarrative,
    selectChoice,
    setCanAdvance,
    isPlayingCredits,
    creditsData
  } = useStoryStore();

  // Debug logging
  useEffect(() => {
    console.log('[StoryPlayer] State:', {
      isLoading,
      isChoicePanelVisible,
      isTransitioning,
      hasNarrative: !!currentNarrative,
      narrativeId: currentNarrative?.id,
      hasChoices: availableChoices.length > 0
    });
  }, [isLoading, isChoicePanelVisible, isTransitioning, currentNarrative, availableChoices]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent shortcuts during transitions
      if (isTransitioning || isLoading) return;

      switch (e.key) {
        case 'Escape':
          onOpenMenu?.();
          break;
        case 'Enter':
        case ' ':
          // Advance narrative on Enter or Space
          if (!isChoicePanelVisible && !isPlayingCredits) {
            e.preventDefault();
            advanceNarrative();
          }
          break;
        // Number keys 1-9 for choices
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          if (isChoicePanelVisible) {
            const choiceIndex = parseInt(e.key) - 1;
            if (availableChoices[choiceIndex]) {
              selectChoice(availableChoices[choiceIndex].id);
            }
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    isTransitioning,
    isLoading,
    isChoicePanelVisible,
    availableChoices,
    advanceNarrative,
    selectChoice,
    onOpenMenu,
    currentScene,
    isPlayingCredits
  ]);

  // Handle click to advance
  const handleContainerClick = () => {
    if (!isChoicePanelVisible && !isTransitioning && !isLoading && !isPlayingCredits) {
      advanceNarrative();
    }
  };

  // If we have a credits sequence, render it exclusively
  if (isPlayingCredits && creditsData) {
    return (
      <div className="story-player relative w-full h-screen overflow-hidden bg-black">
         <CreditsRenderer 
            credits={creditsData} 
            onComplete={() => {
                console.log("Credits complete");
                // You might want to trigger 'Story Complete' state here, or navigate to main menu
                // For now, let's open the menu
                onOpenMenu?.();
            }}
         />
         
         {/* Allow accessing menu during credits */}
         <div className="absolute top-4 right-4 pointer-events-auto z-[60]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenMenu?.();
            }}
            className="p-3 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-colors"
            title="Menu (ESC)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="story-player relative w-full h-screen overflow-hidden bg-black"
      onClick={handleContainerClick}
    >
      {/* Audio Manager (no visual output) */}
      <AudioManager />

      {/* Auto Advance Manager (no visual output) */}
      <AutoAdvanceManager />

      {/* Scene Background (video/image) with Shake Effect */}
      {currentScene && (
        <ShakeEffect>
          <SceneRenderer />
        </ShakeEffect>
      )}

      {/* Narrative Video (plays during specific narratives) */}
      <AnimatePresence>
        {currentNarrative?.video && (
          <NarrativeVideo
            key={currentNarrative.id}
            video={currentNarrative.video}
            onVideoEnded={() => {
              console.log('[StoryPlayer] Narrative video ended, enabling canAdvance');
              setCanAdvance(true);
              (window as any).handleNarrativeMediaEnded?.();
            }}
          />
        )}
      </AnimatePresence>

      {/* Narrative Image (displays during specific narratives) */}
      {/* Key by image URL so same image across narratives doesn't re-animate */}
      <AnimatePresence mode="wait">
        {currentNarrative?.image && (
          <NarrativeImage
            key={currentNarrative.image.url}
            image={currentNarrative.image}
          />
        )}
      </AnimatePresence>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="text-white text-2xl">Loading...</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Story Completed Overlay */}
      <AnimatePresence>
        {isStoryCompleted && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center bg-black/80 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="text-center text-white">
              <h1 className="text-4xl font-bold mb-4">Story Complete</h1>
              <p className="text-xl mb-8">Thank you for playing</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMenu?.();
                }}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-semibold transition-colors"
              >
                Return to Menu
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transition Overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            className="absolute inset-0 bg-black z-40 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
      </AnimatePresence>

      {/* UI Overlay */}
      <div className="absolute inset-0 pointer-events-none z-[60]">
        {/* Top Bar - Scene Title */}
        <AnimatePresence>
          {currentScene && !isLoading && !isStoryCompleted && (
            <motion.div
              className="absolute top-0 left-0 right-0 p-3 sm:p-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-white text-xs sm:text-sm font-semibold tracking-wider uppercase opacity-70 bg-black/30 backdrop-blur-sm px-3 py-2 rounded inline-block">
                {currentScene.title}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Settings/Menu Button */}
        <div className="absolute top-4 right-4 pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenMenu?.();
            }}
            className="p-3 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-colors"
            title="Menu (ESC)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Bottom UI Container */}
        <div className="absolute bottom-0 left-0 right-0 p-6 pb-8 pointer-events-auto">
          {/* Dialogue Box */}
          <AnimatePresence>
            {isDialogueBoxVisible && currentNarrative && (
              <DialogueBox />
            )}
          </AnimatePresence>

          {/* Choice Panel */}
          <AnimatePresence>
            {isChoicePanelVisible && availableChoices.length > 0 && (
              <ChoicePanel />
            )}
          </AnimatePresence>
        </div>

        {/* Advance Indicator (when can advance) */}
        <AnimatePresence>
          {!isChoicePanelVisible && !isTransitioning && !isLoading && currentNarrative && (
            <motion.div
              className="absolute bottom-8 right-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="text-white/50 text-sm"
                animate={{ y: [0, 5, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                ▼
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StoryPlayer;
